import { Avatar } from "@admin/components/atoms/Avatar";
import { useCurrentUserGroups } from "@admin/hooks/useCurrentUserGroups";
import { useLicensesControl } from "@admin/hooks/useLicensesControl";
import { useCommonStore } from "@enos-monorepo/lib";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { SidebarFooter } from "./SidebarFooter";
import { SidebarGroupItem } from "./SidebarGroupItem";

export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSuperAdmin } = useCurrentUserGroups();
  const { company, setCompany } = useCommonStore();

  const shouldShowCompanyMenu = (company && isSuperAdmin) || !isSuperAdmin;

  const { shift, food, isPending } = useLicensesControl();

  const superAdminMenuItems = [
    {
      title: t("navigation.Super Admin"),
      icon: <i className="ki-filled ki-mouse-square"></i>,
      subMenuItems: [
        {
          href: "/super-admin/all-companies",
          label: t("navigation.All Companies"),
        },
      ],
    },
  ];

  const menuItems = [
    {
      title: t("navigation.Company"),
      icon: <i className="ki-filled ki-users"></i>,
      show: true,
      subMenuItems: [
        {
          href: "/",
          label: t("navigation.Dashboard"),
        },
        {
          href: "/company/locations",
          label: t("navigation.Locations"),
        },
        {
          href: "/company/personnel",
          label: t("common.Personnel"),
        },
        {
          href: "/company/settings",
          label: t("navigation.Settings"),
        },
      ],
    },
    {
      title: t("navigation.Shift Management"),
      icon: <i className="ki-filled ki-chart-line-star"></i>,
      show: shift && !isPending,
      subMenuItems: [
        {
          href: "/shift-management/shift-planning",
          label: t("navigation.Shift Planning"),
        },
        {
          href: "/shift-management/todays-shifts",
          label: t("navigation.Today's Shifts"),
        },
        {
          href: "/shift-management/tomorrows-shifts",
          label: t("navigation.Tomorrow's Shifts"),
        },
        {
          href: "/shift-management/past-shifts",
          label: t("navigation.Past Shifts"),
        },
        {
          href: "/shift-management/shift-reports",
          label: t("navigation.Shift Reports"),
        },
      ],
    },
    {
      title: t("navigation.Food Management"),
      icon: <i className="ki-filled ki-profile-circle"></i>,
      show: food && !isPending,
      subMenuItems: [
        {
          href: "/food-management/control-points",
          label: t("navigation.Control Points"),
        },
        {
          href: "/food-management/food-management-report",
          label: t("navigation.Food Management Report"),
        },
        ...(isSuperAdmin
          ? [
              {
                href: "/food-management/egenkontrol-settings",
                label: t("location.Egenkontrol Settings"),
              },
            ]
          : []),
        {
          href: "/food-management/information-bank",
          label: t("riskAnalyses.Risk Analysis"),
        },
      ],
    },
  ];

  return (
    <div
      className="fixed top-0 bottom-0 z-20 hidden lg:flex flex-col shrink-0 w-[--tw-sidebar-width] bg-white dark:bg-coal-300"
      data-drawer="true"
      data-drawer-class="drawer drawer-start flex top-0 bottom-0"
      data-drawer-enable="true|lg:false"
      id="sidebar"
    >
      <div id="sidebar_header">
        <Link to="/">
          <div className="flex items-center gap-2.5 px-3.5 h-[70px]">
            <img
              alt="Enos Logo"
              className="dark:hidden h-[42px]"
              src="/media/logos/enos-logo-small.svg"
            />
            <img
              alt="Enos Logo Dark"
              className="hidden dark:inline-block h-[42px]"
              src="/media/logos/enos-logo-small.svg"
            />
            <div className="menu menu-default grow" data-menu="true">
              <div
                className="menu-item grow"
                data-menu-item-offset="0px, 15px"
                data-menu-item-placement="bottom-start"
                data-menu-item-toggle="dropdown"
              >
                <div className="cursor-pointer text-gray-900 font-medium grow justify-between">
                  <span className="text-base font-medium text-gray-900 grow justify-start">
                    enos Management
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
      <div
        className="flex items-stretch grow shrink-0 justify-center my-5"
        id="sidebar_menu"
      >
        <div
          className="scrollable-y-auto light:[--tw-scrollbar-thumb-color:var(--tw-content-scrollbar-color)] grow"
          data-scrollable="true"
          data-scrollable-dependencies="#sidebar_header, #sidebar_footer"
          data-scrollable-height="auto"
          data-scrollable-offset="0px"
          data-scrollable-wrappers="#sidebar_menu"
        >
          <div
            className="menu flex flex-col w-full gap-1.5 px-3.5"
            data-menu="true"
            data-menu-accordion-expand-all="true"
            id="sidebar_primary_menu"
          >
            {isSuperAdmin &&
              superAdminMenuItems.map((props) => (
                <SidebarGroupItem key={props.title} {...props} />
              ))}

            {company && (
              <div className="text-sm p-2 flex justify-between items-center bg-primary-light rounded-lg">
                <div className="flex items-center gap-2">
                  <Avatar src={company?.avatarPhotoPath} alt="Company Logo" />
                  {company?.companyName}
                </div>
                <i
                  className="ki-filled ki-cross cursor-pointer text-red-500 text-lg"
                  onClick={() => {
                    setCompany();
                    navigate("/super-admin/all-companies");
                  }}
                />
              </div>
            )}
            {shouldShowCompanyMenu &&
              menuItems
                .filter((mI) => mI.show)
                .map((props) => (
                  <SidebarGroupItem key={props.title} {...props} />
                ))}
          </div>
        </div>
      </div>
      <SidebarFooter />
    </div>
  );
}
